/**
 * @module AdapterDetailPage
 * @package @geenius/adapters/solidjs-css
 * @description Renders the single-domain adapter detail view for the SolidJS
 * CSS variant. The page surfaces current provider health, available providers,
 * and inline configuration for one adapter domain.
 */

import type { AdapterDomain, DomainAdapterConfig } from "@geenius/adapters";
import {
  DOMAIN_DESCRIPTIONS,
  DOMAIN_ICONS,
  DOMAIN_LABELS,
  getProvidersForDomain,
} from "@geenius/adapters";
import type { JSX } from "solid-js";
import {
  createMemo,
  createSignal,
  createUniqueId,
  ErrorBoundary,
  For,
  Show,
} from "solid-js";
import { AdapterConfigForm } from "../components/AdapterConfigForm";
import { AdapterStatusBadge } from "../components/AdapterStatusBadge";
import { cx } from "../lib/cx";
import { createAdapters } from "../primitives";
import { useAdapterContext } from "../provider";
import { PageBoundaryFallback } from "./PageBoundaryFallback";

/**
 * Props for rendering the SolidJS CSS single-domain adapter detail page.
 *
 * @property domain Adapter domain whose provider details should be displayed.
 * @property onBack Optional callback for returning to the dashboard view.
 * @property onConfigure Optional callback used to persist submitted provider configuration.
 * @property initialConfig Existing configuration value used to hydrate the inline form.
 * @property class Optional class name merged into the page container.
 */
export interface AdapterDetailPageProps {
  domain: AdapterDomain;
  onBack?: () => void;
  onConfigure?: (
    domain: AdapterDomain,
    config: DomainAdapterConfig,
  ) => Promise<void> | void;
  initialConfig?: DomainAdapterConfig;
  class?: string;
}

/**
 * Renders the SolidJS CSS adapter detail page for one adapter domain.
 *
 * @param props Page props including the selected domain and optional back-navigation handler.
 * @returns The detail view with provider health, available providers, and configuration affordances.
 */
export function AdapterDetailPage(props: AdapterDetailPageProps): JSX.Element {
  return (
    <ErrorBoundary
      fallback={(error, reset) => (
        <PageBoundaryFallback
          class={props.class}
          error={error}
          onRetry={reset}
          title={`${DOMAIN_LABELS[props.domain]} adapter failed to load`}
        />
      )}
    >
      <AdapterDetailPageContent {...props} />
    </ErrorBoundary>
  );
}

function AdapterDetailPageContent(props: AdapterDetailPageProps): JSX.Element {
  const adapters = createAdapters();
  const context = useAdapterContext();
  // Re-read status and provider list reactively against `props.domain` so the
  // detail page does not stay pinned to the domain it mounted with.
  const status = createMemo(() => context.statuses[props.domain]);
  const providers = createMemo(() => getProvidersForDomain(props.domain));
  const [isEditing, setIsEditing] = createSignal(false);
  const latency = () => status().latency ?? 0;
  const label = (): string => DOMAIN_LABELS[props.domain] ?? props.domain;
  const latencyLabelId = createUniqueId();

  return (
    <div
      class={cx(
        "gn-adapters-page",
        "gn-adapters-page--narrow",
        adapters.isLoading ? "gn-adapters-page--loading" : "",
        props.class,
      )}
    >
      <Show
        when={!adapters.isLoading}
        fallback={
          <div
            aria-busy="true"
            aria-label={`Loading ${label()} adapter`}
            class="gn-adapters-loading"
            role="status"
          >
            <div class="gn-adapters-skeleton gn-adapters-skeleton--subtitle" />
            <div class="gn-adapters-skeleton gn-adapters-skeleton--hero" />
            <div class="gn-adapters-skeleton gn-adapters-skeleton--panel" />
          </div>
        }
      >
        <Show when={props.onBack}>
          <button
            type="button"
            onClick={() => props.onBack?.()}
            class="gn-adapters-back-button"
          >
            ← Back to Adapters
          </button>
        </Show>

        <div class="gn-adapters-detail__header">
          <div aria-hidden="true" class="gn-adapters-detail__icon">
            {DOMAIN_ICONS[props.domain]}
          </div>
          <div class="gn-adapters-detail__body">
            <div class="gn-adapters-detail__title-row">
              <h1 class="gn-adapters-page__title">{label()}</h1>
              <AdapterStatusBadge status={status().status} />
            </div>
            <p class="gn-adapters-page__subtitle">
              {DOMAIN_DESCRIPTIONS[props.domain]}
            </p>
          </div>
        </div>

        <div class="gn-adapters-summary__card">
          <h2 class="gn-adapters-summary__label">Current Status</h2>
          <div class="gn-adapters-detail__status-grid">
            <div>
              <p class="gn-adapters-detail__label">Provider</p>
              <p class="gn-adapters-detail__value">
                {status().provider !== "none" ? status().provider : "None"}
              </p>
            </div>
            <div>
              <p class="gn-adapters-detail__label">Status</p>
              <div class="gn-adapters-detail__value">
                <AdapterStatusBadge status={status().status} />
              </div>
            </div>
            <div>
              <p class="gn-adapters-detail__label">Last Checked</p>
              <p class="gn-adapters-detail__value">
                <Show when={status().lastCheckedAt} fallback={"Not checked"}>
                  {(lastCheckedAt) =>
                    new Date(lastCheckedAt()).toLocaleTimeString()
                  }
                </Show>
              </p>
            </div>
          </div>
          <Show when={status().error}>
            <div
              class="gn-adapters-alert gn-adapters-alert--error gn-adapters-alert--spaced"
              role="alert"
            >
              <strong>Error:</strong> {status().error}
            </div>
          </Show>
          <Show when={status().latency != null}>
            <div class="gn-adapters-detail__latency">
              <p class="gn-adapters-detail__label" id={latencyLabelId}>
                Latency
              </p>
              <meter
                aria-labelledby={latencyLabelId}
                aria-valuetext={`${latency()} milliseconds`}
                class={cx(
                  "gn-adapters-detail__meter",
                  latency() < 100
                    ? "gn-adapters-detail__meter--fast"
                    : latency() < 500
                      ? "gn-adapters-detail__meter--medium"
                      : "gn-adapters-detail__meter--slow",
                )}
                max={1000}
                min={0}
                value={Math.min(1000, latency())}
              />
              <p class="gn-adapters-detail__value gn-adapters-detail__value--subtle">
                {latency()}ms
              </p>
            </div>
          </Show>
        </div>

        <div class="gn-adapters-summary__card">
          <div class="gn-adapters-detail__section-header">
            <h2 class="gn-adapters-summary__label">Available Providers</h2>
            <span class="gn-adapters-detail__count">
              {providers().length} providers
            </span>
          </div>
          <div class="gn-adapters-provider-list">
            <For each={providers()}>
              {(p) => (
                <div class="gn-adapters-provider-list__item">
                  <div>
                    <p class="gn-adapters-detail__value gn-adapters-detail__value--strong">
                      {p.name}
                    </p>
                    <p class="gn-adapters-detail__label">{p.description}</p>
                  </div>
                  <span
                    class={cx(
                      "gn-adapters-tier-badge",
                      `gn-adapters-tier-badge--${p.tier}`,
                    )}
                  >
                    {p.tier}
                  </span>
                </div>
              )}
            </For>
          </div>
        </div>

        <Show
          when={isEditing()}
          fallback={
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              class="gn-adapters-card gn-adapters-card--action"
            >
              Configure {label()} Adapter
            </button>
          }
        >
          <AdapterConfigForm
            domain={props.domain}
            initialConfig={props.initialConfig}
            onSave={async (config) => {
              await props.onConfigure?.(props.domain, config);
              setIsEditing(false);
            }}
            onCancel={() => setIsEditing(false)}
          />
        </Show>
      </Show>
    </div>
  );
}
